import { createEffect, on, onCleanup, onMount, untrack, type JSX } from "solid-js";
import { unwrap } from "solid-js/store";
import type ApexCharts from "apexcharts";
import type { ApexOptions } from "apexcharts";

/**
 * The one ApexCharts mount point — an owned wrapper, deliberately NOT
 * `solid-apexcharts`.
 *
 * Ported from the c9erp-web design system (`src/core/ui/charts/apex.tsx`), whose
 * contract exists because `solid-apexcharts` dereferences its chart instance
 * unguarded across an async init: `onCleanup` calls `chart.destroy()` (and the
 * update effects call `updateSeries`/`updateOptions`) while `chart` can still be
 * undefined, because init awaits the dynamic apexcharts import. Disposal happens
 * routinely moments after mount, and Solid runs cleanups UN-caught, so that race
 * bubbles the throw past the chart's own ErrorBoundary to whoever triggered the
 * disposal. Hence:
 *   - every chart-instance touch is guarded on the instance existing;
 *   - teardown can never throw (a failed destroy of a dying chart is harmless);
 *   - async failures are LOGGED, never left as unhandled rejections.
 *
 * ApexCharts is browser-only and heavy (~1.8 MB), so it stays a dynamic import
 * resolved ONCE per session; init picks up the latest props when the import
 * lands, so pre-init prop changes need no queued updates.
 */

type ApexSeries = NonNullable<ApexOptions["series"]>;
type ApexChartType = NonNullable<NonNullable<ApexOptions["chart"]>["type"]>;

let apexModule: Promise<typeof ApexCharts> | null = null;
const loadApex = () => (apexModule ??= import("apexcharts").then((m) => m.default));

export function ApexChart(props: {
  type: ApexChartType;
  options: ApexOptions;
  series: ApexSeries;
  width?: string | number;
  height?: string | number;
}): JSX.Element {
  let el!: HTMLDivElement;
  let chart: ApexCharts | undefined;
  let disposed = false;

  // Deliberately untracked: init resolves async, so it snapshots the LATEST props
  // at that moment; the `on(...)` effects below own all reactivity.
  const config = (): ApexOptions =>
    untrack(() => ({
      ...props.options,
      // Caller options win where they overlap (they already carry height).
      chart: { type: props.type, width: props.width, height: props.height, ...props.options.chart },
      // ApexCharts deep-extends its inputs in place — never hand it a live store node.
      series: unwrap(props.series),
    }));

  const teardown = () => {
    try {
      chart?.destroy();
    } catch {
      // Swallowed by design: this runs inside Solid's un-caught cleanup path,
      // where a throw would take down the whole disposing surface.
    }
    chart = undefined;
  };

  const init = () =>
    void loadApex()
      .then((Apex) => {
        if (disposed) return;
        chart = new Apex(el, config());
        return chart.render();
      })
      .catch((err) => console.error("[charts] ApexCharts failed to initialise:", err));

  onMount(init);

  // Pre-init prop changes fall through the `chart` guard on purpose: init reads
  // config() at resolution time, so the freshest series/options render first.
  createEffect(
    on(
      () => props.series,
      (series) => {
        if (!chart) return;
        void chart
          .updateSeries(unwrap(series))
          .catch((err: unknown) => console.error("[charts] updateSeries failed:", err));
      },
      { defer: true },
    ),
  );

  createEffect(
    on(
      () => props.options,
      (options) => {
        if (!chart) return;
        void chart
          .updateOptions({ ...options })
          .catch((err: unknown) => console.error("[charts] updateOptions failed:", err));
      },
      { defer: true },
    ),
  );

  // A type/size change is a re-create in ApexCharts, not an update.
  createEffect(
    on(
      () => [props.type, props.width, props.height] as const,
      () => {
        if (!chart) return; // init in flight already picks up the new value
        teardown();
        init();
      },
      { defer: true },
    ),
  );

  onCleanup(() => {
    disposed = true;
    teardown();
  });

  return <div ref={el} />;
}
